import { InputWithVariableInsert } from "@/components/input-with-variable-insert/InputWithVariableInsert.tsx";
import { getWorkflowPlaceholderOptions } from "@/components/rich-editor/rich-editor.types.ts";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { ApiWorkflowEventTypes } from "@/types/workflows/workflow.base-enum.types.ts";
import i18n from "@/i18n.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Modal, ModalProps, Select, Stack, useModals } from "@skailer-com/uikit";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

export interface HTTPRequestHeaderValues {
  key: string;
  value: string;
}

const STANDARD_HEADER_NAMES = [
  "Content-Type",
  "Authorization",
  "Accept",
  "Accept-Language",
  "Accept-Encoding",
  "Cache-Control",
  "User-Agent",
  "X-Api-Key",
  "X-Auth-Token",
  "X-Request-Id",
];

const SIGNATURE_HEADER_NAME = "X-Webhook-Signature";
const HEADER_NAME_PATTERN = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;
const FORBIDDEN_HEADER_NAMES = new Set([
  "host",
  "content-length",
  "connection",
  "transfer-encoding",
  "upgrade",
]);

export interface WorkflowEditActionHTTPRequestHeaderModalProps extends ModalProps {
  header?: HTTPRequestHeaderValues;
  existingKeys: string[];
  eventType?: ApiWorkflowEventTypes | null;
  onSave: (header: HTTPRequestHeaderValues) => void;
}

const buildSchema = (existingKeys: string[]) =>
  yup.object({
    key: yup
      .string()
      .required(i18n.t("errors:field_required"))
      .test(
        "header-key-valid",
        i18n.t("workflow:action_type_http_request_header_invalid"),
        (value) => HEADER_NAME_PATTERN.test((value ?? "").trim())
      )
      .test(
        "header-key-forbidden",
        i18n.t("workflow:action_type_http_request_header_forbidden"),
        (value) => {
          const normalized = (value ?? "").trim().toLowerCase();
          return !FORBIDDEN_HEADER_NAMES.has(normalized) && !normalized.startsWith("proxy-");
        }
      )
      .test(
        "header-key-reserved",
        i18n.t("workflow:action_type_http_request_header_reserved"),
        (value) => (value ?? "").trim().toLowerCase() !== SIGNATURE_HEADER_NAME.toLowerCase()
      )
      .test(
        "header-key-duplicate",
        i18n.t("workflow:action_type_http_request_header_duplicate"),
        (value) => {
          const normalized = (value ?? "").trim().toLowerCase();
          return !existingKeys.some((key) => key.trim().toLowerCase() === normalized);
        }
      ),
    value: yup.string().required(i18n.t("errors:field_required")),
  });

export const WorkflowEditActionHTTPRequestHeaderModal = ({
  header,
  existingKeys,
  eventType,
  onSave,
  ...props
}: WorkflowEditActionHTTPRequestHeaderModalProps) => {
  const { close } = useModals();
  const { id } = props;
  const { t } = useTranslation();

  const {
    control,
    register,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<HTTPRequestHeaderValues>({
    mode: "onSubmit",
    resolver: yupResolver(buildSchema(existingKeys)),
    values: {
      key: header?.key ?? "",
      value: header?.value ?? "",
    },
  });

  const keyValue = watch("key");
  const keyOptions = useMemo(() => {
    const base = STANDARD_HEADER_NAMES.map((name) => ({ id: name, title: name }));
    if (keyValue && !STANDARD_HEADER_NAMES.includes(keyValue)) {
      return [{ id: keyValue, title: keyValue }, ...base];
    }
    return base;
  }, [keyValue]);

  const handleSave = handleSubmit((data) => {
    onSave({ key: data.key.trim(), value: data.value });
    close(id);
  });

  return (
    <Modal
      {...props}
      variant={"middle"}
      title={t("workflow:action_type_http_request_header_key")}
      layout={"base"}
      withCloser
      closeByEscEnabled
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button size={"lg"} className={"w-full"} type={"submit"} onClick={handleSave}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={handleSave}>
        <Stack gap={"lg"}>
          <Controller
            control={control}
            name={"key"}
            render={({ field: { value } }) => (
              <Select
                label={t("workflow:action_type_http_request_header_key")}
                emptyTitle={t("common:select")}
                searchable={true}
                required={true}
                value={value}
                options={keyOptions}
                onChange={(selected) => setValue("key", selected ? String(selected) : "")}
                onAddNew={(text) => setValue("key", text.trim())}
                addNewPrefix={t("common:add")}
                error={errors.key?.message}
              />
            )}
          />
          <InputWithVariableInsert
            label={t("workflow:action_type_http_request_header_value")}
            type="text"
            {...register("value")}
            required={true}
            error={errors.value?.message}
            variableOptions={getWorkflowPlaceholderOptions(eventType)}
            disableSpacing
          />
        </Stack>
      </form>
    </Modal>
  );
};
